// react
import { useContext, useState } from "react";
// libs
import { motion, Variants } from "framer-motion";
import styled, { css } from "styled-components";
// components
import { Box } from "../Box";
import { ImageFill } from "../ImageFill";
import { Text } from "../Text";
import Tooltip from "../Tooltip";
// context
import { ArtifactViewSelectionContext } from "../../context/ArtifactViewSelectionContext";
// other
import { InteractiveInnerSlide } from "../../models/client";

interface ATInnerSlideProps {
  innerSlide: InteractiveInnerSlide;
}

const ImgContainer = styled(motion.div)`
  position: relative;
  padding-top: 100%;
  box-shadow: 5px 5px 20px 0px rgb(0 0 0 / 14%);
  background-color: white;
  cursor: pointer;
`;

const SlideTemp = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const LongName = styled(Box)`
  ${({ theme }) => css`
    ${theme.mediaQueries.lg} {
      position: absolute;
      top: 7px;
      left: -2.5em;
      transform: rotate(-90deg) translateX(-100%);
      transform-origin: top left;
    }
  `}
`;

const MotionRotationBox = styled(motion(Box))``;

const tempVars: Variants = {
  hiddenFront: { rotate: "-4deg", scale: 1.05, y: 50, x: 10 },
  front: { opacity: 1, scale: 1, rotate: "-2deg", y: 0, pointerEvents: "all" },
  second: { opacity: 1, scale: 1, rotate: "1deg", zIndex: 1, y: 0 },
  third: { opacity: 1, scale: 1, rotate: "2deg", zIndex: 1, y: 0 },
  hiddenBack: {
    opacity: 0,
    zIndex: 0,
    pointerEvents: "none",
    y: -50,
    scale: 0.9,
  },
};

const longNameVars: Variants = {
  hiddenFront: { opacity: 0 },
  front: { opacity: 1 },
  second: { opacity: 0 },
  third: { opacity: 0 },
  hiddenBack: { opacity: 0 },
};

const TooltipPosition = styled(motion.div)`
  position: absolute;
  bottom: 32px;
  left: 32px;
  z-index: 1;
`;

function ATInnerSlide({ innerSlide }: ATInnerSlideProps) {
  const [hovered, setHovered] = useState(false);
  const { setArtifactById } = useContext(ArtifactViewSelectionContext);

  const handleMouseOver = () => {
    if (!hovered) {
      setHovered(true);
    }
  };

  return (
    <SlideTemp>
      <MotionRotationBox variants={tempVars} transition={{ duration: 0.5 }}>
        <ImgContainer
          whileHover={{ scale: 1.01, rotate: "0.5deg" }}
          whileTap={{ scale: 1, rotate: "0.25deg" }}
          onClick={() => setArtifactById(innerSlide.id)}
          onMouseOver={handleMouseOver}
          onMouseLeave={() => setHovered(false)}
        >
          <ImageFill
            src={innerSlide.previewImg.src}
            srcLow={`${innerSlide.previewImg.src}?width=400`}
            alt={innerSlide.previewImg.title}
            fit={innerSlide.previewImg.fit}
          />
          <TooltipPosition>
            <Tooltip icon={innerSlide.cursorIcon} expanded={hovered}>
              {innerSlide.cursorText}
            </Tooltip>
          </TooltipPosition>
        </ImgContainer>
      </MotionRotationBox>
      <LongName mt={3}>
        <motion.div variants={longNameVars} transition={{ duration: 0.5 }}>
          <Text variant="small" fontSize={1}>
            {innerSlide.title}
          </Text>
        </motion.div>
      </LongName>
    </SlideTemp>
  );
}

export default ATInnerSlide;
